/**
 * The regime → rate defaults table, and the statutory rates everything seeds from.
 *
 * This is the **only** place a tax rate is written as a literal in this client.
 * Everywhere else — payroll, dividends — reads the entity's stored settings. That
 * is the whole point of Task 09 Part A: a rate at a call site is a defect even
 * when the number is currently right.
 *
 * Rates are basis points (1 bp = 1/10000), so 20% is `2000` and 0% is `0`.
 */

import type {
    LedgerSettings,
    RegimeDrivenRate,
    RateOverrides,
    TaxRates,
    TaxRegime,
} from '@/api/models';

/**
 * What each regime dictates.
 *
 * Only the two distribution rates are regime-driven. Payroll and VAT are
 * statutory and identical across regimes, so a regime change must not touch them
 * — a Virtual Zone company still withholds 20% PIT from its staff.
 *
 * | Regime                 | CIT on distribution | Dividend WHT |
 * | ---------------------- | ------------------- | ------------ |
 * | Standard LLC           | 15%                 | 5%           |
 * | Virtual Zone           | 0%                  | 5%           |
 * | Free Industrial Zone   | 0%                  | 5%           |
 * | International Company  | 5%                  | 0%           |
 */
export const REGIME_DEFAULTS: Record<
    TaxRegime,
    Record<RegimeDrivenRate, number>
> = {
    STANDARD: {
        corporateIncomeTaxBps: 1500,
        dividendWithholdingTaxBps: 500,
    },
    VIRTUAL_ZONE: {
        corporateIncomeTaxBps: 0,
        dividendWithholdingTaxBps: 500,
    },
    FREE_INDUSTRIAL_ZONE: {
        corporateIncomeTaxBps: 0,
        dividendWithholdingTaxBps: 500,
    },
    INTERNATIONAL_COMPANY: {
        corporateIncomeTaxBps: 500,
        dividendWithholdingTaxBps: 0,
    },
};

/** Every regime, in the order the settings screen offers them. */
export const TAX_REGIMES: TaxRegime[] = [
    'STANDARD',
    'VIRTUAL_ZONE',
    'FREE_INDUSTRIAL_ZONE',
    'INTERNATIONAL_COMPANY',
];

/** The rates that a regime dictates, in the order the screen shows them. */
export const REGIME_DRIVEN_RATES: RegimeDrivenRate[] = [
    'corporateIncomeTaxBps',
    'dividendWithholdingTaxBps',
];

/**
 * The statutory payroll and VAT rates. Not regime-driven — the same under every
 * regime — but still settings, so an entity can record a rate change without a
 * release.
 */
export const STATUTORY_RATES = {
    /** Personal income tax: flat 20% on gross. */
    personalIncomeTaxBps: 2000,
    /** Employee pension: 2%, withheld. */
    employeePensionBps: 200,
    /** Employer pension: 2%, a company cost. */
    employerPensionBps: 200,
    /** State pension: up to 2%, information only — never journalized. */
    statePensionBps: 200,
    /** VAT standard rate: 18%. */
    vatStandardBps: 1800,
} as const;

/** The full rate set a regime implies, before any manual override. */
export const ratesForRegime = (regime: TaxRegime): TaxRates => ({
    ...STATUTORY_RATES,
    ...REGIME_DEFAULTS[regime],
});

/**
 * Which stored rates differ from their regime's default.
 *
 * Derived on every read rather than stored, so the flag can never fall out of step
 * with the rates themselves — the classic way an "edited" badge ends up lying.
 */
export const deriveOverrides = (
    regime: TaxRegime,
    rates: TaxRates,
): RateOverrides => {
    const defaults = REGIME_DEFAULTS[regime];
    const overrides: RateOverrides = {};
    for (const key of REGIME_DRIVEN_RATES) {
        overrides[key] = rates[key] !== defaults[key];
    }
    return overrides;
};

/**
 * The seeded entity: **STANDARD**.
 *
 * Deliberately the regime that exercises the full calculation, gross-up included
 * — a 0% regime would collapse the CIT arithmetic to zero and hide an error in it.
 * Switching to a zone regime is then one field on the settings screen, with no
 * code change, which is exactly what acceptance criterion 4 checks.
 */
export const SEED_REGIME: TaxRegime = 'STANDARD';

export const seedLedgerSettings = (): LedgerSettings => {
    const rates = ratesForRegime(SEED_REGIME);
    return {
        regime: SEED_REGIME,
        rates,
        overrides: deriveOverrides(SEED_REGIME, rates),
    };
};
